"use client";

import { Button } from "@xefi/x-react/button";
import { IconFileExport } from "@xefi/x-react/icons";
import { addToast } from "@xefi/x-react/toast";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useExportPayments } from "@/hooks/useExportPayments";
import {
  usePaymentsDateRange,
  usePaymentsDomaIds,
  usePaymentsSubsidiaryId,
} from "@/store/usePaymentsFiltersStore";
import type {
  MissingCardPaymentState,
  PaymentExportCategory,
  PaymentsDoneState,
} from "@/types/payments";
import { getApiErrorMessage } from "@/utils/apiError";
import { toScopeParams } from "./paymentsQuery";

type Props = {
  category: PaymentExportCategory;
  done?: PaymentsDoneState;
  search?: string;
  state?: MissingCardPaymentState;
  dealerships?: string[];
};

export default function ExportButton({
  category,
  done = "all",
  search,
  state,
  dealerships,
}: Props) {
  const { t } = useTranslation();
  const subsidiaryId = usePaymentsSubsidiaryId();
  const domaIds = usePaymentsDomaIds();
  const dateRange = usePaymentsDateRange();

  const { mutate, isPending } = useExportPayments();

  const scope = useMemo(
    () => toScopeParams({ subsidiaryId, domaIds, dateRange }),
    [subsidiaryId, domaIds, dateRange],
  );

  // Absent, le paramètre laisse l'export porter les deux états de « FAIT ».
  const doneParam = useMemo(
    () => (done === "all" ? {} : { done: done === "yes" }),
    [done],
  );

  const tableViewParams = useMemo(() => {
    const term = search?.trim();

    return {
      ...(term ? { search: term } : {}),
      ...(state ? { state } : {}),
      ...(dealerships && dealerships.length > 0 ? { dealerships } : {}),
    };
  }, [search, state, dealerships]);

  const handleExport = () => {
    mutate(
      { type: category, ...scope, ...doneParam, ...tableViewParams },
      {
        onError: (error) =>
          addToast({
            title: getApiErrorMessage(error, t("notifications.error.export")),
            color: "danger",
          }),
      },
    );
  };

  return (
    <Button
      variant="bordered"
      color="primary"
      size="sm"
      radius="sm"
      isLoading={isPending}
      onClick={handleExport}
      startContent={!isPending && <IconFileExport size={16} />}
    >
      {t("paymentsReceived.actions.export")}
    </Button>
  );
}
